/**
 * Service API pour la gestion des paiements
 * Utilise des appels API côté serveur pour la génération de signatures
 */

import { euroToXofAmount } from './currency';

class PaymentApiService {
  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    console.log('API Base URL:', this.apiBaseUrl);
    console.log('Environment variables:', {
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
      NODE_ENV: process.env.NODE_ENV
    });
  }

  /**
   * Génère une signature pour les paramètres CyberSource
   * @param {Object} params - Les paramètres à signer
   * @returns {Promise<string>} - La signature
   */
  async generateSignature(params) {
    try {
      const url = `${this.apiBaseUrl}/payment/generate-signature`;
      console.log('Making request to:', url);
      console.log('Request params:', params);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      return data.signature;
    } catch (error) {
      console.error('Erreur lors de la génération de signature:', error);
      // Fallback: signature simple pour les tests
      return this.generateSimpleSignature(params);
    }
  }

  /**
   * Génère une signature simple pour les tests
   * @param {Object} params - Les paramètres
   * @returns {string} - Signature simple
   */
  generateSimpleSignature(params) {
    const signedFieldNames = params.signed_field_names.split(',');
    const dataToSign = signedFieldNames
      .map(field => `${field}=${params[field] || ''}`)
      .join(',');
    
    const timestamp = Date.now().toString();
    const simpleHash = btoa(dataToSign + timestamp);
    return simpleHash.substring(0, 32);
  }

  /**
   * Construit les paramètres de paiement pour CyberSource
   * @param {Object} paymentData - Les données de paiement du formulaire
   * @param {Object} pricingData - Les données de tarification
   * @returns {Promise<Object>} - Les paramètres complets pour CyberSource
   */
  async buildPaymentParams(paymentData, pricingData, explicitLocale) {
    const transactionUuid = this.generateTransactionUuid();
    const signedDateTime = this.getSignedDateTime();
    
    // Champs signés (doivent être inclus dans signed_field_names)
    const signedFields = [
      'profile_id',
      'access_key', 
      'transaction_uuid',
      'signed_field_names',
      'unsigned_field_names',
      'signed_date_time',
      'locale',
      'transaction_type',
      'reference_number',
      'auth_trans_ref_no',
      'amount',
      'currency',
      'merchant_descriptor',
      'override_custom_cancel_page',
      'override_custom_receipt_page'
    ];

    // Champs non signés (correspondant exactement à votre PHP)
    const unsignedFields = [
      'signature',
      'bill_to_forename',
      'bill_to_surname', 
      'bill_to_email',
      'bill_to_phone',
      'bill_to_address_line1',
      'bill_to_address_line2',
      'bill_to_address_city',
      'bill_to_address_state',
      'bill_to_address_country',
      'bill_to_address_postal_code',
      'customer_ip_address',
      'merchant_defined_data1',
      'merchant_defined_data2',
      'merchant_defined_data3',
      'merchant_defined_data4'
    ];

    const referenceNumber = this.generateReferenceNumber();
    const authTransRefNo = this.generateReferenceNumber(); // Différent de reference_number
    const amount = euroToXofAmount(pricingData.price);
    
    const userLocale = (explicitLocale || (navigator?.language || 'fr-FR')).toLowerCase().startsWith('fr') ? 'fr-fr' : 'en-us';
    const params = {
      // Champs de base CyberSource
      profile_id: process.env.REACT_APP_CYBERSOURCE_PROFILE_ID,
      access_key: process.env.REACT_APP_CYBERSOURCE_ACCESS_KEY,
      transaction_uuid: transactionUuid,
      signed_date_time: signedDateTime,
      signed_field_names: signedFields.join(','),
      unsigned_field_names: unsignedFields.join(','),
      
      // Configuration de la transaction
      transaction_type: 'sale',
      reference_number: referenceNumber,
      auth_trans_ref_no: authTransRefNo, // Différent de reference_number
      amount: amount,
      currency: 'XOF',
      locale: userLocale,
      merchant_descriptor: process.env.REACT_APP_MERCHANT_DESCRIPTOR || 'put_merchant_name_here',
      
      // URLs de redirection
      override_custom_cancel_page: `${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001'}/api/payment/cancel`,
      override_custom_receipt_page: `${process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001'}/api/payment/success`,
      
      // Informations de facturation
      bill_to_forename: paymentData.firstName,
      bill_to_surname: paymentData.lastName,
      bill_to_email: paymentData.email,
      bill_to_phone: paymentData.phone,
      bill_to_address_line1: paymentData.address,
      bill_to_address_line2: '', // Toujours vide comme dans PHP
      bill_to_address_city: '', // Vide comme dans la payload PHP qui fonctionne
      bill_to_address_state: paymentData.city || '', // La ville va dans state (comme "Dakar" dans votre PHP)
      bill_to_address_country: this.getCountryCode(paymentData.country),
      bill_to_address_postal_code: paymentData.postalCode,
      
      // Données techniques
      customer_ip_address: '', // Sera rempli côté serveur ou laissé vide
      
      // Données marchand personnalisées
      merchant_defined_data1: 'MDD#1',
      merchant_defined_data2: 'MDD#2',
      merchant_defined_data3: 'MDD#3',
      merchant_defined_data4: 'MDD#4'
    };

    // Générer la signature
    params.signature = await this.generateSignature(params);
    
    return params;
  }

  /**
   * Génère un UUID unique pour la transaction (format simple comme PHP)
   * @returns {string} - UUID de transaction
   */
  generateTransactionUuid() {
    // Format simple comme "6939e81193ef1" au lieu d'un UUID complet
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `${timestamp}${random}`;
  }

  /**
   * Génère un numéro de référence unique
   * @returns {string} - Numéro de référence
   */
  generateReferenceNumber() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `TV${timestamp}${random}`;
  }

  /**
   * Obtient la date/heure signée au format ISO
   * @returns {string} - Date/heure au format ISO
   */
  getSignedDateTime() {
    return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  /**
   * Convertit le nom du pays en code ISO
   * @param {string} countryName - Nom du pays
   * @returns {string} - Code ISO du pays
   */
  getCountryCode(countryName) {
    if (typeof countryName === 'string' && /^[A-Za-z]{2}$/.test(countryName.trim())) {
      return countryName.trim().toUpperCase();
    }
    const countryCodes = {
      'Sénégal': 'SN',
      'France': 'FR', 
      'Côte d\'Ivoire': 'CI',
      'Mali': 'ML',
      'Burkina Faso': 'BF',
      'Autre': 'SN'
    };
    return countryCodes[countryName] || 'SN';
  }

  /**
   * Génère un ID d'empreinte digitale pour le device
   * @returns {string} - ID d'empreinte digitale
   */
  generateDeviceFingerprintId() {
    // Génération d'un ID d'empreinte digitale simple
    const timestamp = Date.now().toString(16);
    const random = Math.random().toString(16).substring(2);
    return `${timestamp}${random}`.substring(0, 32);
  }

  /**
   * Génère le paramètre device_fingerprint_param pour CyberSource
   * Format: org_id=1snn5n9w&session_id=ECOEGH0002bb40439147fe62daa100c6f6b79cee20
   * @returns {string} - Paramètre device fingerprint
   */
  generateDeviceFingerprintParam() {
    const orgId = 'TRANSVIE'; // DF_ORG_ID du .env
    const merchantId = 'ecoesn0093'; // MERCHANT_ID du .env
    const sessionId = this.generateDeviceFingerprintId(); // ID de session unique
    return `org_id=${orgId}&session_id=${merchantId}${sessionId}`;
  }

  /**
   * Soumet le formulaire de paiement à CyberSource
   * @param {Object} paymentData - Données de paiement
   * @param {Object} pricingData - Données de tarification
   * @returns {Promise<Object>} - Résultat de la soumission
   */
  async submitPayment(paymentData, pricingData, explicitLocale) {
    try {
      console.log('🔄 Début de la soumission du paiement...');
      
      const params = await this.buildPaymentParams(paymentData, pricingData, explicitLocale);
      console.log('✅ Paramètres générés:', Object.keys(params));
      
      // Remplir les champs cachés du formulaire existant
      const form = document.getElementById('payment_form');
      if (!form) {
        console.error('❌ Formulaire de paiement non trouvé');
        throw new Error('Formulaire de paiement non trouvé');
      }
      
      console.log('✅ Formulaire trouvé, remplissage des champs...');
      
      // Remplir tous les champs cachés avec les paramètres
      Object.keys(params).forEach(key => {
        const input = form.querySelector(`input[name="${key}"]`);
        if (input) {
          input.value = params[key];
          console.log(`✅ Champ ${key} rempli: ${params[key]}`);
        } else {
          console.warn(`⚠️ Champ ${key} non trouvé dans le formulaire`);
        }
      });
      
      // URL CyberSource
      const cyberSourceUrl = process.env.REACT_APP_CYBERSOURCE_PAYMENT_URL || 'https://testsecureacceptance.cybersource.com/pay';
      console.log('🌐 URL CyberSource:', cyberSourceUrl);
      
      // Modifier l'action du formulaire pour pointer vers CyberSource
      form.action = cyberSourceUrl;
      form.method = 'POST';

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3005/api';
      const referencePhone = paymentData.referencePhone
        ? (String(paymentData.referencePhone).startsWith('+') ? paymentData.referencePhone : `+${paymentData.referencePhone}`)
        : '';
      try {
        await fetch(`${apiBase}/payment/context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referenceNumber: params.reference_number,
            referenceFirstName: paymentData.referenceFirstName || '',
            referenceLastName: paymentData.referenceLastName || '',
            referencePhone,
          }),
        });
      } catch (contextError) {
        console.error('Contexte personne de référence non enregistré', contextError);
      }
      
      console.log('🚀 Soumission du formulaire vers CyberSource...');
      
      // Soumettre le formulaire
      form.submit();
      
      return {
        success: true,
        message: 'Redirection vers CyberSource...',
        params: params
      };
      
    } catch (error) {
      console.error('❌ Erreur lors de la soumission du paiement:', error);
      return {
        success: false,
        message: 'Erreur lors de la soumission du paiement',
        error: error.message
      };
    }
  }

  /**
   * Valide les données de paiement avant soumission
   * @param {Object} paymentData - Données de paiement
   * @returns {Object} - Résultat de la validation
   */
  validatePaymentData(paymentData) {
    const errors = {};
    
    if (!paymentData.firstName || paymentData.firstName.trim() === '') {
      errors.firstName = 'Prénom requis';
    }
    
    if (!paymentData.lastName || paymentData.lastName.trim() === '') {
      errors.lastName = 'Nom de famille requis';
    }
    
    if (!paymentData.email || !/\S+@\S+\.\S+/.test(paymentData.email)) {
      errors.email = 'Email invalide';
    }
    
    if (!paymentData.phone || paymentData.phone.trim() === '') {
      errors.phone = 'Numéro de téléphone requis';
    }
    
    if (!paymentData.address || paymentData.address.trim() === '') {
      errors.address = 'Adresse requise';
    }
    
    if (!paymentData.city || paymentData.city.trim() === '') {
      errors.city = 'Ville requise';
    }
    
    if (!paymentData.postalCode || paymentData.postalCode.trim() === '') {
      errors.postalCode = 'Code postal requis';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors: errors
    };
  }
}

const paymentApiService = new PaymentApiService();
export default paymentApiService;
